<script>
  import SelecaoBusca from './SelecaoBusca.vue';
  import AnexosOperacao from './AnexosOperacao.vue';

  export default {
    components: { SelecaoBusca, AnexosOperacao },
    props: {
      value: { type: Array, required: true },
      total: { type: Number, default: 0 },
      buscar: { type: Function, required: true },
      validar: { type: Boolean, default: false },
      dicaOrigem: { type: String, default: '' },
      comObservacao: { type: Boolean, default: false },
      anexo: { type: Object, default: null },
    },
    computed: {
      multi() { return this.value.length > 1; },
      observacaoPorPerna() { return this.comObservacao && this.multi; },
      distribuido() {
        return this.arredondar(this.value.reduce((soma, p) => soma + (+p.valor || 0), 0));
      },
      saldo() { return this.arredondar(this.total - this.distribuido); },
      fechou() { return Math.abs(this.saldo) < 0.005; },
      paresRepetidos() {
        const chaves = this.value.filter(p => p.idContaOrigem > 0 && p.idContaDestino > 0)
          .map(p => p.idContaOrigem + '-' + p.idContaDestino);
        return chaves.some((c, i) => chaves.indexOf(c) !== i);
      },
      mensagem() {
        if (this.value.some(p => !(p.idContaOrigem > 0) || !(p.idContaDestino > 0)))
          return 'Informe a conta de débito e a de crédito de cada transferência.';
        if (this.value.some(p => p.idContaOrigem === p.idContaDestino))
          return 'A conta de crédito deve ser diferente da de débito.';
        if (this.paresRepetidos) return 'O mesmo par de contas aparece duas vezes — junte os valores numa linha só.';
        if (this.value.some(p => !((+p.valor || 0) > 0))) return 'O valor de cada transferência precisa ser maior que zero.';
        if (!this.fechou)
          return this.saldo > 0
            ? 'Falta distribuir ' + this.formatar(this.saldo) + ' entre as transferências.'
            : 'As transferências passaram ' + this.formatar(-this.saldo) + ' do valor do débito.';
        if (this.observacaoPorPerna && this.value.some(p => !(p.observacao || '').trim()))
          return 'Informe a observação de cada transferência.';
        return '';
      },
      valido() { return this.mensagem === ''; },
    },
    watch: {
      valido: {
        immediate: true,
        handler(v) { this.$emit('valido', v); },
      },
      total() {
        if (this.value.length === 1) this.definirValor(this.value[0], this.total);
      },
      'value.length'() {
        if (this.value.length === 1) this.definirValor(this.value[0], this.total);
      },
    },
    mounted() {
      if (this.value.length === 0) this.adicionar();
      else if (this.value.length === 1) this.definirValor(this.value[0], this.total);
    },
    methods: {
      novaPerna(valor) {
        return {
          idContaOrigem: 0, nomeContaOrigem: '',
          idContaDestino: 0, nomeContaDestino: '',
          valor: valor > 0 ? this.arredondar(valor) : 0,
          valorTexto: valor > 0 ? this.formatar(valor) : '',
          observacao: '', observacaoSugerida: '',
          anexos: [],
        };
      },
      adicionar() {
        this.$emit('input', this.value.concat([this.novaPerna(this.saldo)]));
      },
      remover(indice) {
        const linhas = this.value.slice();
        linhas.splice(indice, 1);
        this.$emit('input', linhas);
      },
      aoDigitarValor(perna, evento) {
        perna.valorTexto = evento.target.value;
        perna.valor = this.converter(evento.target.value);
      },
      aoSairDoValor(perna) { this.definirValor(perna, perna.valor); },
      usarSaldo(perna) { this.definirValor(perna, (+perna.valor || 0) + this.saldo); },
      definirValor(perna, valor) {
        const novo = this.arredondar(valor > 0 ? valor : 0);
        perna.valor = novo;
        perna.valorTexto = novo > 0 ? this.formatar(novo) : '';
      },
      converter(texto) {
        const limpo = (texto || '').replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
        return this.arredondar(+limpo || 0);
      },
      arredondar(valor) { return Math.round((+valor || 0) * 100) / 100; },
      formatar(valor) {
        return (+valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
  }
</script>

<template>
  <div class="pt-bloco">
    <div v-if="multi" class="pt-topo">
      <span class="pt-titulo">Transferências geradas</span>
      <span class="pt-resumo" :class="{ 'pt-resumo-ok': fechou, 'pt-resumo-falta': !fechou }">
        {{ formatar(distribuido) }} de {{ formatar(total) }}
        <template v-if="!fechou"> · falta {{ formatar(saldo) }}</template>
      </span>
    </div>

    <div v-for="(perna, indice) in value" :key="indice" class="pt-item" :class="{ 'pt-item-multi': multi }">
      <div v-if="multi" class="pt-faixa">
        <span class="pt-ordem">Transferência {{ indice + 1 }} de {{ value.length }}</span>
        <button type="button" class="pt-remover" @click="remover(indice)" title="Remover esta transferência">
          <i class="fa fa-trash-o"></i>
        </button>
      </div>

      <div class="pt-linha">
        <div class="pt-campo" :class="{ 'pt-invalido': validar && !(perna.idContaOrigem > 0) }">
          <label>Conta de débito <span class="pt-obr">*</span></label>
          <selecao-busca v-model="perna.idContaOrigem" :texto="perna.nomeContaOrigem" :buscar="buscar"
                         placeholder="Conta de onde sai o valor..."
                         @selecionado="o => perna.nomeContaOrigem = o ? o.nome : ''"></selecao-busca>
          <small v-if="indice === 0 && dicaOrigem" class="pt-dica">{{ dicaOrigem }}</small>
        </div>
        <div class="pt-campo" :class="{ 'pt-invalido': validar && !(perna.idContaDestino > 0) }">
          <label>Conta de crédito <span class="pt-obr">*</span></label>
          <selecao-busca v-model="perna.idContaDestino" :texto="perna.nomeContaDestino" :buscar="buscar"
                         placeholder="Conta que deveria ter recebido..."
                         @selecionado="o => perna.nomeContaDestino = o ? o.nome : ''"></selecao-busca>
        </div>
        <div v-if="multi" class="pt-campo pt-campo-valor" :class="{ 'pt-invalido': validar && !(perna.valor > 0) }">
          <label>Valor</label>
          <div class="pt-valor-caixa">
            <input type="text" class="form-control pt-valor" :value="perna.valorTexto"
                   @input="aoDigitarValor(perna, $event)" @blur="aoSairDoValor(perna)" placeholder="0,00">
            <button v-if="!fechou" type="button" class="pt-saldo" @click="usarSaldo(perna)"
                    title="Jogar o que falta nesta transferência"><i class="fa fa-level-down"></i></button>
          </div>
        </div>
      </div>

      <div v-if="observacaoPorPerna" class="pt-linha pt-linha-obs">
        <div class="pt-campo pt-campo-obs" :class="{ 'pt-invalido': validar && !(perna.observacao || '').trim() }">
          <label>Observação desta transferência <span class="pt-obr">*</span></label>
          <input type="text" class="form-control" maxlength="300" v-model="perna.observacao"
                 placeholder="O que explicar a quem vai liberar esta transferência">
        </div>
      </div>

      <div v-if="anexo" class="pt-anexo">
        <anexos-operacao v-model="perna.anexos" :limite="anexo.limite || 0" :origens="anexo.origens || null"
                         :titulo="multi ? (anexo.titulo || 'Arquivos') + ' · transferência ' + (indice + 1) : (anexo.titulo || 'Arquivos')"
                         :dica="anexo.dica || ''"></anexos-operacao>
      </div>
    </div>

    <div class="pt-rodape">
      <button type="button" class="pt-add" @click="adicionar">
        <i class="fa fa-plus"></i> {{ multi ? 'Adicionar outra transferência' : 'Dividir em mais transferências' }}
      </button>
      <span v-if="validar && mensagem" class="pt-msg">{{ mensagem }}</span>
    </div>
  </div>
</template>

<style scoped>
  .pt-bloco { margin-top: 4px; }
  .pt-topo { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px; }
  .pt-titulo { font-size: 12px; font-weight: 600; color: #34495e; }
  .pt-item-multi { border: 1px solid #dbe3ec; border-left: 3px solid #2f6fb2; border-radius: 6px; padding: 0 10px 10px; background: #fff; }
  .pt-item-multi ~ .pt-item-multi { margin-top: 12px; }
  .pt-faixa { display: flex; align-items: center; justify-content: space-between; margin: 0 -10px 6px;
              padding: 5px 10px; background: #f2f6fa; border-bottom: 1px solid #e3e8ef; border-radius: 0 5px 0 0; }
  .pt-ordem { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; }
  .pt-remover { border: 0; background: none; color: #b0bac5; cursor: pointer; padding: 0 2px; }
  .pt-remover:hover { color: #c0392b; }
  .pt-linha { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; }
  .pt-linha-obs { margin-top: 8px; }
  .pt-anexo { margin-top: 10px; }
  .pt-campo-obs { flex: 1 1 100%; }
  .pt-campo { flex: 1 1 220px; min-width: 0; }
  .pt-campo-valor { flex: 0 0 140px; }
  .pt-campo label { display: block; font-size: 11px; color: #64748b; margin-bottom: 2px; font-weight: 600; }
  .pt-obr { color: #c0392b; }
  .pt-dica { display: block; font-size: 11px; color: #8b97a5; margin-top: 2px; }
  .pt-valor-caixa { position: relative; }
  .pt-valor { text-align: right; padding-right: 22px; }
  .pt-saldo { position: absolute; right: 3px; top: 50%; transform: translateY(-50%); border: 0; background: none;
              color: #8b97a5; padding: 0 2px; font-size: 11px; cursor: pointer; }
  .pt-saldo:hover { color: #2f6fb2; }
  .pt-invalido .form-control, .pt-invalido >>> .form-control { border-color: #c0392b; background: #fdf6f5; }
  .pt-invalido label { color: #c0392b; }
  .pt-rodape { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; }
  .pt-add { border: 1px solid #cfd8e3; background: #fff; border-radius: 3px; color: #2f6fb2;
            font-size: 11px; padding: 3px 9px; cursor: pointer; }
  .pt-add:hover { border-color: #2f6fb2; }
  .pt-msg { font-size: 11px; color: #c0392b; }
  .pt-resumo { font-size: 11px; font-weight: 600; }
  .pt-resumo-ok { color: #27865b; }
  .pt-resumo-falta { color: #b9552c; }
</style>



// WEBPACK FOOTER //
// FrontEnd/modulos/conciliacao-projeto/componentes/PernasTransitoria.vue